import {SearchAnalytics, SearchContext} from './search-analytics';
import {HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig} from '../../config/hotel-tours-list-page.config';
import {WindowRefService} from '../window-ref.service';
import {Inject, Injectable} from "@angular/core";
import {ToursStateService} from "../tours-state.service";
import {environment} from "../../../environments/environment";
import {BehaviorSubject, Observable, of, Subject} from "rxjs";
import {
  HotelToursCalendarCell,
  HotelToursCalendarRequest,
  HotelToursCalendarResponse,
  HotelToursCalendarRow,
  HotelToursSearchParams,
  HotelToursSearchRequest,
  HotelToursSearchResponse
} from "../../types/interfaces/hotel-tour-search";
import {HttpHotelToursSearchService} from "./hotel-tours-requests.service";
import {EventBusService} from "../event-bus.service";
import {HotelTourCalendarNightsService} from "../hotel-tour-calendar/nights.service";
import {finalize, shareReplay, tap} from "rxjs/operators";

export interface PriceCalendarDateRange {
  dateFrom: string;
  dateTo: string;
}

@Injectable({
  providedIn: 'root',
})
export class HotelToursSearchService {
  private analytics: SearchAnalytics;
  private requestVersion = 0;
  private lastSearchSource: 'default' | 'filters' | 'form' | 'calendar' | 'empty_dates' = 'default';
  private newSearchStartedSub = new BehaviorSubject<string>('');
  private searchProgressTimeoutSub = new Subject<number>();
  private firstToursFoundSub = new BehaviorSubject<boolean>(false);
  private toursNotFoundSub = new BehaviorSubject<boolean>(false);
  private lastSearchParams: HotelToursSearchParams = null;
  private searchStartFailedSub = new BehaviorSubject<boolean>(false);
  private searchIsDoneSub = new Subject<string>();
  private isLoadFiltersSub = new Subject<string>();
  private selectedFilters: any;
  private preferredCalendarStartDate: string | null = null;
  private mobileBootstrapCalendarKey: string | null = null;
  private mobileBootstrapCalendarResponse: HotelToursCalendarResponse | null = null;
  private mobileCalendarBootstrapLoading = false;
  private mobileCalendarBootstrapLoadingSub = new BehaviorSubject<boolean>(false);
  private priceCalendarCache = new Map<string, HotelToursCalendarResponse>();
  private priceCalendarRequests = new Map<string, Observable<HotelToursCalendarResponse>>();
  private priceCalendarCacheVersion = 0;
  private latestPriceCalendarResponse: HotelToursCalendarResponse | null = null;
  private latestPriceCalendarRanges: PriceCalendarDateRange[] = [];
  private latestPriceCalendarSub = new BehaviorSubject<HotelToursCalendarResponse | null>(null);
  private latestPriceCalendarRangesSub = new BehaviorSubject<PriceCalendarDateRange[]>([]);
  private latestPriceCalendarLoadingSub = new BehaviorSubject<boolean>(false);

  newSearchStarted$ = this.newSearchStartedSub.asObservable();
  isLoadFilters$ = this.isLoadFiltersSub.asObservable();
  searchStartFailed$ = this.searchStartFailedSub.asObservable();
  searchIsDone$ = this.searchIsDoneSub.asObservable();
  mobileCalendarBootstrapLoading$ = this.mobileCalendarBootstrapLoadingSub.asObservable();
  toursNotFound$ = this.toursNotFoundSub.asObservable();
  firstToursFound$ = this.firstToursFoundSub.asObservable();
  latestPriceCalendar$ = this.latestPriceCalendarSub.asObservable();
  latestPriceCalendarRanges$ = this.latestPriceCalendarRangesSub.asObservable();
  latestPriceCalendarLoading$ = this.latestPriceCalendarLoadingSub.asObservable();

  constructor(
    private state: ToursStateService,
    private hotelToursSearch: HttpHotelToursSearchService,
    private hotelTourCalendarNightsService: HotelTourCalendarNightsService,
    private eventBusService: EventBusService,
    private windowRef: WindowRefService,
    @Inject(HOTEL_TOURS_PAGE_CONFIG) private appConfig: HotelToursPageConfig,
  ) {
    this.analytics = new SearchAnalytics(this.windowRef.nativeWindow, 'hotel', environment.production);
  }

  startNewSearchByHotelId(formParams: any, source: 'default' | 'filters' | 'form' | 'calendar' | 'empty_dates' = 'default') {
    this.lastSearchSource = source;

    if (source !== 'filters') {
      this.selectedFilters = [];
    }

    const searchRequest = this.createSearchRequest(formParams);
    const context = this.analytics.begin({...searchRequest.params, hotel_id: this.appConfig.hotel.hotelId}, source === 'default' ? 'initial' : source === 'form' ? 'search_form' : source);
    const version = ++this.requestVersion;

    this.clearPriceCalendarCache({ resetLatest: !environment.mobile || source === 'calendar' });
    this.newSearchStartedSub.next('');
    this.searchStartFailedSub.next(false);
    this.firstToursFoundSub.next(false);
    this.toursNotFoundSub.next(false);
    this.state.clearState();

    this.hotelToursSearch
      .startSearchByHotelId(searchRequest)
      .subscribe({
        next: (response: HotelToursSearchResponse) => {
          if (version !== this.requestVersion) { return; }
          if (response?.error || !response?.id) {
            this.trackSearchError(response?.error || 'Search response has no id', context);
            this.eventBusService.hideToursLoader();
            this.searchStartFailedSub.next(true);
            return;
          }
          this.newSearchStartedSub.next(response.id);
          this.searchProgressTimeoutSub.next(response.timeout);
          if (source !== 'calendar') { this.eventBusService.reloadCalendar$.next(true); }
        },
        error: (error) => {
          if (version !== this.requestVersion) { return; }
          this.trackSearchError(error, context);
          this.eventBusService.hideToursLoader();
          this.searchStartFailedSub.next(true);
        },
      });
  }

  trackPageOpen(params: any): void {
    this.analytics.openPage({...params, hotel_id: this.appConfig.hotel.hotelId});
  }

  trackAlternativeDates(context: SearchContext, count: number): void {
    this.analytics.alternativeDates(context, count);
  }

  trackSearchError(error: any, context = this.analytics.context()): void {
    const candidates = [error?.error?.error, error?.error?.message, error?.error, error?.message, error];
    const message = candidates.find(value => typeof value === 'string' && value.trim() !== '')
      || 'Unknown hotel search error';
    this.analytics.result('search_result_error', context, undefined, {error_message: message});
  }

  getAnalyticsContext() { return this.analytics.context(); }

  getRequestVersion(): number { return this.requestVersion; }

  isCurrentRequest(version: number): boolean { return version === this.requestVersion; }

  trackSearchResult(event: string, context = this.analytics.context()) {
    this.analytics.result(event, context);
  }

  setLastSearchParams(formParams) {
    this.createSearchRequest(formParams);
  }

  getNormalizedSearchParams(formParams: any): HotelToursSearchParams {
    return this.createSearchRequest(formParams).params;
  }

  private createSearchRequest(formParams: any): HotelToursSearchRequest {
    let dateFrom = this.getDate(formParams.dateFrom);
    let dateTo = this.getDate(formParams.dateTo);
    if (formParams.forceDates === 1) {
      dateFrom = this.getDate(formParams.dateFrom);
      dateTo = this.getDate(formParams.dateTo);
    }

    const params: HotelToursSearchParams = {
      adults: Number(formParams.adults),
      childAges: formParams.childAges,
      countryId: formParams.country ? formParams.country : formParams.countryId,
      dateFrom: dateFrom,
      dateTo: dateTo,
      ...(formParams.hotelsSearch ? {} : {departCityId: formParams.departCity || formParams.departCityId}),
      hotelsSearch: formParams.hotelsSearch ? 1 : 0,
      nightsFrom: formParams.nightsFrom,
      nightsTo: formParams.nightsTo,
      daysFrom: formParams.daysFrom,
      daysTo: formParams.daysTo,
      airlines: formParams.airlineCodes,
      meals: formParams.mealIds,
      splitRooms: false,
      from: formParams.from,
    };

    const filters = this.getSelectedFilters();

    if (filters.airlines) {
      params.airlines = filters.airlines;
    } else {
      if (Array.isArray(formParams.airlineCodes)) {
        params.airlines = formParams.airlineCodes;
      } else if (formParams.airlineCodes) {
        params.airlines = [formParams.airlineCodes];
      } else {
        params.airlines = [];
      }
    }

    if (filters.meals) {
      params.meals = filters.meals;
    } else {
      if (Array.isArray(formParams.mealIds)) {
        params.meals = formParams.mealIds;
      } else if (formParams.mealIds) {
        params.meals = [formParams.mealIds];
      } else {
        params.meals = []; // оставляем пустой массив, если formParams.airlines пустой или не существует
      }
    }

    if (formParams.splitRooms) {
      params.splitRooms = formParams.splitRooms;
    }

    if (formParams.forceDates) {
      params.forceDates = formParams.forceDates;
    }

    this.lastSearchParams = params;
    (window as typeof window & {lastSearchParams?: HotelToursSearchParams}).lastSearchParams = params;

    return {params};
  }

  private getDate(value) {
    if (!value) {
      return value;
    }

    if (environment.mobile) {
      const date = new Date(value);

      let year = date.getFullYear(); // Получаем год
      let month = String(date.getMonth() + 1).padStart(2, '0'); // Получаем месяц и добавляем ведущий ноль
      let day = String(date.getDate()).padStart(2, '0'); // Получаем день и добавляем ведущий ноль

      return `${year}-${month}-${day}`; // Формируем строку в формате Y-m-d
    }

    if (value instanceof Date) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    const strValue = String(value);
    if (strValue.includes('T')) {
      return strValue.split('T')[0];
    }
    if (strValue.includes('-')) {
      return strValue.slice(0, 10);
    }
    if (strValue.includes('.')) {
      return strValue.split('.').reverse().join('-');
    }

    return strValue;
  }

  getLastSearchParams(): HotelToursSearchParams {
    return this.lastSearchParams;
  }

  getLastSearchSource() {
    return this.lastSearchSource;
  }

  searchIsDone(searchId: string) {
    this.searchIsDoneSub.next(searchId);
  }

  isLoadFilters(searchId: string) {
    this.isLoadFiltersSub.next(searchId);
  }

  notFoundTours() {
    this.toursNotFoundSub.next(true);
  }

  firstToursFound(): void {
    if (this.firstToursFoundSub.value === false) {
      this.firstToursFoundSub.next(true);
    }
  }

  setSelectedFilters(filters: any) {
    this.selectedFilters = filters;
  }

  setPreferredCalendarStartDate(date: string | null) {
    this.preferredCalendarStartDate = date;
  }

  setMobileBootstrapCalendar(key: string, response: HotelToursCalendarResponse | null) {
    this.mobileBootstrapCalendarKey = key;
    this.mobileBootstrapCalendarResponse = response;
  }

  getMobileBootstrapCalendar(key: string) {
    if (this.mobileBootstrapCalendarKey !== key) {
      return null;
    }

    return this.mobileBootstrapCalendarResponse;
  }

  getCachedHotelPriceCalendar(
    bodyParams: HotelToursCalendarRequest,
    options: { updateLatest?: boolean; publishLatest?: boolean } = {}
  ): Observable<HotelToursCalendarResponse> {
    const updateLatest = options.updateLatest !== false;
    const publishLatest = options.publishLatest !== false;
    const cacheKey = this.hotelToursSearch.getHotelPriceCalendarCacheKey(bodyParams);
    const cachedResponse = this.priceCalendarCache.get(cacheKey);

    if (cachedResponse) {
      if (publishLatest) {
        this.publishLatestPriceCalendarRange(bodyParams);
        this.publishLatestPriceCalendar(cachedResponse);
      }

      if (updateLatest) {
        this.latestPriceCalendarLoadingSub.next(false);
      }

      return of(cachedResponse);
    }

    const activeRequest = this.priceCalendarRequests.get(cacheKey);
    if (activeRequest) {
      if (updateLatest) {
        this.latestPriceCalendarLoadingSub.next(true);
      }

      if (publishLatest) {
        const activeCacheVersion = this.priceCalendarCacheVersion;

        return activeRequest.pipe(
          tap((response) => {
            if (this.priceCalendarCacheVersion === activeCacheVersion) {
              this.publishLatestPriceCalendarRange(bodyParams);
              this.publishLatestPriceCalendar(response);
            }
          })
        );
      }

      return activeRequest;
    }

    const cacheVersion = this.priceCalendarCacheVersion;
    let request$: Observable<HotelToursCalendarResponse>;

    if (updateLatest) {
      this.latestPriceCalendarLoadingSub.next(true);
    }

    request$ = this.hotelToursSearch.getHotelPriceCalendar(bodyParams)
      .pipe(
        tap((response) => {
          if (this.priceCalendarCacheVersion === cacheVersion) {
            this.priceCalendarCache.set(cacheKey, response);
            if (publishLatest) {
              this.publishLatestPriceCalendarRange(bodyParams);
              this.publishLatestPriceCalendar(response);
            }
          }
        }),
        finalize(() => {
          if (updateLatest && this.priceCalendarCacheVersion === cacheVersion) {
            this.latestPriceCalendarLoadingSub.next(false);
          }

          if (
            this.priceCalendarCacheVersion === cacheVersion
            && this.priceCalendarRequests.get(cacheKey) === request$
          ) {
            this.priceCalendarRequests.delete(cacheKey);
          }
        }),
        shareReplay(1)
      );

    this.priceCalendarRequests.set(cacheKey, request$);

    return request$;
  }

  publishHotelPriceCalendarResponse(bodyParams: HotelToursCalendarRequest, response: HotelToursCalendarResponse) {
    this.publishLatestPriceCalendarRange(bodyParams);
    this.publishLatestPriceCalendar(response);
  }
  getLatestPriceCalendarSnapshot(): HotelToursCalendarResponse | null {
    return this.latestPriceCalendarResponse;
  }

  getLatestPriceCalendarRangesSnapshot(): PriceCalendarDateRange[] {
    return this.latestPriceCalendarRanges;
  }

  getLastMobileBootstrapCalendar() {
    return this.mobileBootstrapCalendarResponse;
  }

  clearMobileBootstrapCalendar() {
    this.mobileBootstrapCalendarKey = null;
    this.mobileBootstrapCalendarResponse = null;
  }

  private clearPriceCalendarCache(options: { resetLatest?: boolean } = {}) {
    const resetLatest = options.resetLatest !== false;

    this.priceCalendarCacheVersion += 1;
    this.priceCalendarCache.clear();
    this.priceCalendarRequests.clear();

    if (resetLatest) {
      this.latestPriceCalendarResponse = null;
      this.latestPriceCalendarRanges = [];
      this.latestPriceCalendarSub.next(null);
      this.latestPriceCalendarRangesSub.next([]);
      this.latestPriceCalendarLoadingSub.next(false);
    }

    this.clearMobileBootstrapCalendar();
    this.setMobileCalendarBootstrapLoading(false);
  }

  private publishLatestPriceCalendar(response: HotelToursCalendarResponse) {
    this.latestPriceCalendarResponse = this.mergePriceCalendarResponses(
      this.latestPriceCalendarResponse,
      response
    );
    this.latestPriceCalendarSub.next(this.latestPriceCalendarResponse);
  }

  private publishLatestPriceCalendarRange(bodyParams: HotelToursCalendarRequest) {
    const range = {
      dateFrom: bodyParams.params.dateFrom,
      dateTo: bodyParams.params.dateTo,
    };

    if (!range.dateFrom || !range.dateTo) {
      return;
    }

    const hasRange = this.latestPriceCalendarRanges.some((item) => {
      return item.dateFrom === range.dateFrom && item.dateTo === range.dateTo;
    });

    if (hasRange) {
      return;
    }

    this.latestPriceCalendarRanges = [...this.latestPriceCalendarRanges, range]
      .sort((first, second) => first.dateFrom.localeCompare(second.dateFrom));
    this.latestPriceCalendarRangesSub.next(this.latestPriceCalendarRanges);
  }

  private mergePriceCalendarResponses(
    current: HotelToursCalendarResponse | null,
    next: HotelToursCalendarResponse
  ): HotelToursCalendarResponse {
    const rowsByNights = new Map<number, Map<string, HotelToursCalendarCell>>();

    this.addPriceCalendarRows(rowsByNights, current?.calendar || []);
    this.addPriceCalendarRows(rowsByNights, next.calendar || []);

    return {
      calendar: Array.from(rowsByNights.entries())
        .sort(([firstNights], [secondNights]) => firstNights - secondNights)
        .map(([nights, cellsByDate]) => ({
          nights,
          prices: Array.from(cellsByDate.values())
            .sort((first, second) => first.date.localeCompare(second.date)),
        })),
    };
  }

  private addPriceCalendarRows(
    rowsByNights: Map<number, Map<string, HotelToursCalendarCell>>,
    rows: HotelToursCalendarRow[]
  ) {
    for (const row of rows) {
      for (const cell of row.prices || []) {
        const nights = Number(cell.nights || row.nights);
        const cellsByDate = rowsByNights.get(nights) || new Map<string, HotelToursCalendarCell>();

        cellsByDate.set(cell.date, cell);
        rowsByNights.set(nights, cellsByDate);
      }
    }
  }

  setMobileCalendarBootstrapLoading(value: boolean) {
    this.mobileCalendarBootstrapLoading = value;
    this.mobileCalendarBootstrapLoadingSub.next(value);
  }

  isMobileCalendarBootstrapLoading() {
    return this.mobileCalendarBootstrapLoading;
  }

  hasCalendarPrices(response: HotelToursCalendarResponse | null) {
    if (!response?.calendar?.length) {
      return false;
    }

    return response.calendar.some((row) =>
      Array.isArray(row.prices) && row.prices.some((price) => Number(price?.price?.value) > 0)
    );
  }

  getSelectedFilters() {
    const filters = this.selectedFilters; // объект вида { key: ['mealIds', 'airlines'], value: ['7', 'kc'] }

    const formattedFilters: { [key: string]: string[] } = {};

    // Проверяем, что filters содержит ключи key и value, и они являются массивами одинаковой длины
    if (filters) {
      for (const filter of filters) {
        const {key, value} = filter;
        if (!formattedFilters[key]) {
          formattedFilters[key] = []; // Инициализируем массив, если его ещё нет
        }
        formattedFilters[key].push(value);
      }
    }

    return formattedFilters;
  }

  getHotelToursCalendarParams(): HotelToursCalendarRequest {
    const nightsFrom = this.lastSearchParams.nightsFrom;
    const nightsTo = this.lastSearchParams.nightsTo;
    const nights = this.hotelTourCalendarNightsService.getNights(nightsFrom);
    if (!environment.mobile) {
      nights.nightsTo = Math.max(nightsTo, nights.nightsTo);
    }

    const dateFrom = this.lastSearchParams.dateFrom;
    const dateTo = this.lastSearchParams.dateTo;

    const from = new Date(dateFrom);
    from.setDate(from.getDate());
    const dateFromStr = `${from.getFullYear()}-${(from.getMonth() + 1).toString().padStart(2, '0')}-${from.getDate().toString().padStart(2, '0')}`;

    const to = new Date(dateTo);

    if (this.lastSearchParams.dateFrom === this.lastSearchParams.dateTo) {
      to.setDate(to.getDate() + 5);
    } else {
      to.setDate(to.getDate() + 1);
    }

    let dateToStr = `${to.getFullYear()}-${(to.getMonth() + 1).toString().padStart(2, '0')}-${to.getDate().toString().padStart(2, '0')}`;
    let calendarDateFrom = dateFromStr;

    if (environment.mobile && this.preferredCalendarStartDate) {
      const preferredStart = new Date(this.preferredCalendarStartDate);
      preferredStart.setHours(0, 0, 0, 0);

      const currentFrom = new Date(dateFromStr);
      currentFrom.setHours(0, 0, 0, 0);

      const currentTo = new Date(dateToStr);
      currentTo.setHours(0, 0, 0, 0);

      const rangeDays = Math.max(
        0,
        Math.round((currentTo.getTime() - currentFrom.getTime()) / 86400000)
      );

      const shiftedTo = new Date(preferredStart);
      shiftedTo.setDate(preferredStart.getDate() + rangeDays);

      calendarDateFrom = `${preferredStart.getFullYear()}-${(preferredStart.getMonth() + 1).toString().padStart(2, '0')}-${preferredStart.getDate().toString().padStart(2, '0')}`;
      dateToStr = `${shiftedTo.getFullYear()}-${(shiftedTo.getMonth() + 1).toString().padStart(2, '0')}-${shiftedTo.getDate().toString().padStart(2, '0')}`;
    }

    return {
      params: {
        adults: this.lastSearchParams.adults,
        childAges: this.lastSearchParams.childAges,
        departCityId: this.lastSearchParams.departCityId,
        countryId: this.lastSearchParams.countryId,
        dateFrom: calendarDateFrom,
        dateTo: dateToStr,
        nightsFrom: nights.nightsFrom,
        nightsTo: nights.nightsTo,
        airlines: this.lastSearchParams.airlines,
      },
    };
  }
}
